import { AppDebug, AppDebugLogType } from "../app/debug/AppDebug";
import { ScreenManager } from "../managers/ScreenManager";
import { TextStatesData, TextureStatesData } from "../types/types";
import { GetBounds } from "../utils/GetBounds";
import { ButtonBase } from "./ButtonBase";


export class LabelIconButton extends ButtonBase
{
    public label: Phaser.GameObjects.Text;
    public icon: Phaser.GameObjects.Image;

    // 
    protected iconTextureDatas: TextureStatesData;
    protected iconScale: number;

    // allows for out/over/disabled states on label (just like on bg)
    protected labelStatesData: TextStatesData;
    
    
    /**
     * ButtonBase with a Label
     * 
     * @example:
     *  myButton = new LabelButton(..)
     *  myButton.InitBase(..)
     *  myButton.InitLabel(..)
     *  myButton.PostInit(..)
     */
    constructor(scene: Phaser.Scene, x?: number, y?: number)
    {
        super(scene, x, y);
    }

    /**
     * 
     * @param outTextureData texture for when mouse is outside of button
     * @param overTextureData texture for when mouse is over button; if this is set to null, 
     *      Button wont have the 'over texture'
     * @param disabledTextureData 
     * @param isInteractive Should button by default be in its enabled state, or disable state?
     *      NOTE: by default, the button is in its enabled state; To change this default state, 
     *            call this function with isInteractive=false;
     * @returns 
     */
    public InitLabel(
        labelStatesData: TextStatesData,
        labelOffset?: Phaser.Geom.Point
    ): void
    {
        this.labelStatesData = labelStatesData;

        this.label = this.scene.make.text({style: labelStatesData.out});
        this.add(this.label);

        if (labelOffset) {
            this.label.setPosition(labelOffset.x, labelOffset.y);
        }
    }

    public InitIcon(
        iconTextureDatas: TextureStatesData,
        iconPosition: Phaser.Geom.Point = null,
        iconScale: number = 1
    ): void
    {
        this.iconTextureDatas = iconTextureDatas;
        this.iconScale = iconScale;
        
        this.icon = this.scene.make.image({key:iconTextureDatas.out.texture, frame:iconTextureDatas.out.frame});
        if (iconPosition) {
            this.icon.setPosition(iconPosition.x, iconPosition.y);
        }
        // this.icon.setScale(iconScale * ScreenManager.svScaleFactor);
        this.icon.setScale(iconScale);
        this.add(this.icon);
    }

    public SetIconTextureData(iconTextureDatas: TextureStatesData): void
    {
        this.iconTextureDatas = iconTextureDatas;
        // force; assume out state (?)
        this.icon.setFrame(iconTextureDatas.out.frame);
        this.UpdateViewFromInteractive();

        // TODO: does this work?
        this.UpdateHitArea();
    }

    public SetLabel(value: string): void
    {
        this.label.text = value;

        // TODO: does this work?
        this.UpdateHitArea();
    }

    protected override UpdateViewFromInteractive():void
    {
        // toggle button texture view update
        super.UpdateViewFromInteractive();

        // was disabled, is enabled
        if (this.isInteractive) {
            // was using disabled texture
            if (this.iconTextureDatas.disabled != null) {
                // set 'out' texture
                this.icon.setTexture(this.iconTextureDatas.out.texture, this.iconTextureDatas.out.frame);
            }
            // if (this.labelStatesData.disabled != null) {
            if (this.labelStatesData.disabled != null) {
                this.label.setStyle(this.labelStatesData.out);
            }
        }
        // was enabled, is disabled
        else {
            // if got disabled texture
            if (this.iconTextureDatas.disabled != null) {
                // set disabled texture
                this.icon.setTexture(this.iconTextureDatas.disabled.texture, this.iconTextureDatas.disabled.frame);
            }
            if (this.labelStatesData.disabled != null) {
                this.label.setStyle(this.labelStatesData.disabled);
            }
        }
    }
    
    protected override OnPointerOver()
    {
        // console.log("ToggleButton::OnPointerOver");

        super.OnPointerOver();

        if (this.isInteractive) {
            if (this.iconTextureDatas.over != null) {
                this.icon.setTexture(this.iconTextureDatas.over.texture, this.iconTextureDatas.over.frame);
            }
            if (this.labelStatesData.over != null) {
                this.label.setStyle(this.labelStatesData.over);
            }
        }
    }
    
    protected override OnPointerOut()
    {
        // console.log("LabelIconButton::OnPointerOut");

        super.OnPointerOut();

        if (this.isInteractive) {
            if (this.iconTextureDatas.out != null) {
                this.icon.setTexture(this.iconTextureDatas.out.texture, this.iconTextureDatas.out.frame);
            }
            if (this.labelStatesData.out != null) {
                this.label.setStyle(this.labelStatesData.out);
            }
        }
    }
    
    public override PostInit(hitAreaExtrusion: number = 0): void
    {
        // > hitAreaExtrusion
        // > listeners (buttonTextureDatas)
        // > SetInteractive
        // > UpdateHitArea
        super.PostInit(hitAreaExtrusion);

        // > listeners (iconTextureDatas)
        if (this.buttonTextureDatas.over == null && (this.labelStatesData.over != null || this.iconTextureDatas.over != null)) {
            this.on(Phaser.Input.Events.GAMEOBJECT_POINTER_OVER, this.OnPointerOver);
            this.on(Phaser.Input.Events.GAMEOBJECT_POINTER_OUT, this.OnPointerOut);
        }
    }

    protected override UpdateHitArea(): void
    {
        super.UpdateHitArea();
        
        const labelRect: Phaser.Geom.Rectangle = GetBounds(this.label, this.hitAreaExtrusion);
        const iconRect: Phaser.Geom.Rectangle = GetBounds(this.icon, this.hitAreaExtrusion);
        Phaser.Geom.Rectangle.Union(this.cachedHitArea, labelRect, this.cachedHitArea);
        Phaser.Geom.Rectangle.Union(this.cachedHitArea, iconRect, this.cachedHitArea);

        if (this.input != null) {
            this.input.hitArea = this.cachedHitArea;
            this.input.hitAreaCallback = this.cachedHitAreaCallback;
        }
    }
    
    public override destroy(): void
    {
        AppDebug.log("LabelIconButton("+this.name+")::destroy", AppDebugLogType.DESTROY);
        
        super.destroy();

        this.label.destroy();
        this.icon.destroy();

        this.label = null;
        this.icon = null;
        this.iconTextureDatas = null;
    }
}